import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useHighestRole } from '../auth/useAuth';
import { getRuntimeConfig } from '../config/runtime-config';
import { useActiveSite } from '../state/useActiveSite';
import type { SiteId } from '../api/v1';
import { useAlertNotificationsStable } from '../state/useAlertNotifications';
import { useFireAlarm } from '../state/useFireAlarm';
import { useEmergencyCapabilities } from '../api/hooks/useFireMode';
import { useLiveStatus } from '../api/hooks/useLiveStatus';
import { useTrajectoryFeedStale } from '../api/hooks/useTrajectoryFeed';
import type { LiveState } from '../api/zenoh-sse';
import { AlertBell } from './AlertBell';
import { FavoritesMenu } from './FavoritesMenu';
import { ReportProblemHost } from './report/ReportProblemHost';
import { openReportProblem } from '../state/report-problem';
import { HelpToggle } from './HelpToggle';
import { useTheme } from '../state/useTheme';
import { useInstallPrompt } from './useInstallPrompt';
import type { ThemePreference } from '../state/user-preferences';
import { TopBarStats } from './TopBarStats';
import { RolePreviewBanner } from './RolePreviewBanner';
import { useRolePreview } from '../auth/useRolePreview';
import { previewRoleLabel } from '../auth/role-preview';
import { SITE_SETTINGS_VIEW_ROLES, hasAnyRole } from '../screens/Metrics/metrics-access';
import {
  IconCheck,
  IconChevronDown,
  IconDownload,
  IconFileText,
  IconLifeBuoy,
  IconLogOut,
  IconMonitor,
  IconMoon,
  IconRotateCcw,
  IconSettings,
  IconSun,
  IconVolume2,
  IconVolumeX,
} from '../design-system/icons';
import './TopBar.css';

const LIVE_STATE_LABEL: Record<LiveState, string> = {
  idle: 'Idle',
  connecting: 'Connecting…',
  open: 'Live',
  closed: 'Disconnected',
  stale: 'Stale',
};

const LIVE_STATE_CLASS: Record<LiveState, string> = {
  idle: 'nx-mqtt-dot--idle',
  connecting: 'nx-mqtt-dot--connecting',
  open: 'nx-mqtt-dot--open',
  closed: 'nx-mqtt-dot--closed',
  stale: 'nx-mqtt-dot--stale',
};

/** Local rather than added to `design-system/icons.tsx`: the icon set
 *  is shared chrome owned elsewhere, and this is the only caller. */
function FlameGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2c1.5 3.5-1 5-1 7a3 3 0 0 0 6 0c0-.7-.2-1.4-.5-2 2 1.8 3.5 4.3 3.5 7a8 8 0 0 1-16 0c0-4.5 3.5-7.5 5-9.5C10 3 11 2.5 12 2z" />
    </svg>
  );
}

/**
 * The way in to fire mode when no alarm is running — a supervisor
 * arranging a drill, or anyone checking whether the site is frozen.
 * While an alarm IS running the status banner carries a much louder
 * version of the same control, so this stays a quiet icon button.
 *
 * Hidden entirely for a caller holding neither emergency capability
 * (`foh_operator`, `service_operator`): the panel would show them two
 * gated explanations and nothing they can press.
 */

function LiveStatusDot() {
  const state = useLiveStatus();
  return (
    <div
      className={`nx-mqtt-dot ${LIVE_STATE_CLASS[state]}`}
      // Gives way on a phone; stays in the laptop's slim bar.
      data-tuck="hide-phone"
      role="status"
      aria-live="polite"
      aria-label={`Realtime connection: ${LIVE_STATE_LABEL[state]}`}
      title={`Realtime: ${LIVE_STATE_LABEL[state]}`}
      data-testid="mqtt-status-dot"
    >
      <span className="nx-mqtt-dot__indicator" aria-hidden="true" />
      <span className="nx-mqtt-dot__label">{LIVE_STATE_LABEL[state]}</span>
    </div>
  );
}

/**
 * Sits beside the connection dot and only exists while the trajectory
 * publisher reports its schedule mirror stale (`TrajectorySnapshot
 * .stale`). The connection can be perfectly "Live" — robot markers
 * still moving — while the paths on the map are frozen at the last
 * good snapshot; without this the frozen paths read as "the robot is
 * idle". Same pill styling as the dot so it reads as part of the same
 * status cluster.
 */
function TrajectoryFeedChip() {
  const feed = useTrajectoryFeedStale();
  if (!feed.stale) return null;
  const age = feed.mirrorAgeMs !== null ? ` (schedule mirror ${Math.round(feed.mirrorAgeMs / 1000)} s old)` : '';
  return (
    <div
      className="nx-mqtt-dot nx-mqtt-dot--stale"
      data-tuck="hide-phone"
      role="status"
      aria-live="polite"
      aria-label={`Trajectory feed stale${age}`}
      title={`Trajectory feed stale${age}: robot paths on the map are frozen at the last good snapshot; robot markers are still live`}
      data-testid="trajectory-feed-stale"
    >
      <span className="nx-mqtt-dot__indicator" aria-hidden="true" />
      <span className="nx-mqtt-dot__label">Trajectory feed stale</span>
    </div>
  );
}

/** Pull the first grapheme of a display name for the avatar bubble. */
function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  // Pick the first character of up to the first two whitespace-separated
  // tokens — e.g. "Jane Cooper" → "JC", "ops" → "O". Graphemes are good
  // enough for ASCII names; this is a small visual cue, not security.
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]![0] ?? '') + (parts[1]![0] ?? '')).toUpperCase();
  }
  return trimmed[0]!.toUpperCase();
}

/** Human label for the role token in the avatar menu. */
function roleLabel(role: string | null | undefined): string {
  if (!role) return 'No role';
  return role.replace(/^nexsoss:/, '').replace(/_/g, ' ');
}

/**
 * Gmail-style avatar menu in the top-right corner. Clicking the avatar
 * opens a dropdown with two items:
 *   - Preferences (links to /profile, the existing preferences screen)
 *   - Sign out
 * Closes on outside click, on Escape, on route change, and on
 * arrow-down to the next item (or arrow-up to the previous). The
 * menu follows WAI-ARIA Authoring Practices for `menu` widgets:
 *   - ArrowDown / ArrowUp move focus between items.
 *   - Home / End jump to the first / last item.
 *   - Escape closes the menu and returns focus to the trigger.
 *   - Tab moves focus out of the menu.
 */
/** Fixed slots in `itemRefs` so arrow keys walk the menu top to
 *  bottom: My activity is 2, Site settings 3, the theme radios take 6-8, "View as…" 9
 *  onward (at most five roles plus "Back to my own role"), and Sign out stays last. */
const VIEW_AS_FIRST_INDEX = 9;
const SIGN_OUT_INDEX = 20;

/** The appearance choices, in the order they read: automatic first. */
const THEME_OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  label: string;
  Icon: (props: { size?: number }) => React.ReactElement;
}> = [
  { value: 'system', label: 'Match my device', Icon: IconMonitor },
  { value: 'light', label: 'Light', Icon: IconSun },
  { value: 'dark', label: 'Dark', Icon: IconMoon },
];

/**
 * The fire control, back in the bar — on a phone only.
 *
 * It moved into the avatar menu on 2026-09-06 because it is not used
 * often and the bar was crowded. That trade is right on a desktop and
 * wrong on a phone: the menu is the phone's only route to it, which
 * makes the one control where a lost second matters two taps deep, on
 * the device somebody is holding when they are away from the desk.
 * Akash's decision (2026-09-06) was to keep the drawer for navigation
 * and put a persistent flame here instead of a bottom tab bar.
 *
 * HIDDEN BY CSS ABOVE THE PHONE TIER, not branched on width in the
 * markup: a width branch needs a resize listener and can disagree with
 * the media query at the boundary. The menu item stays at every width,
 * so on a phone the control is in two places — which is the correct
 * amount for this one.
 */
function PhoneFireButton() {
  const { openFireModePanel, isFireAlarmActive } = useFireAlarm();
  const { canTrigger, canReset } = useEmergencyCapabilities();
  if (!(canTrigger || canReset)) return null;
  return (
    <button
      type="button"
      className="nx-topbar__btn nx-topbar__fire-phone"
      onClick={openFireModePanel}
      aria-label={isFireAlarmActive ? 'Fire mode is active — open the panel' : 'Fire mode'}
      title="Fire mode"
      data-active={isFireAlarmActive ? 'true' : 'false'}
      data-testid="topbar-fire-phone"
    >
      <span aria-hidden="true">▲</span>
    </button>
  );
}

function UserAvatarMenu({ onReportProblem }: { onReportProblem: () => void }) {
  const { user, logout, roles } = useAuth();
  // CALLED HERE, unconditionally, and that placement is load-bearing.
  // `useTheme` is the only code that puts `data-theme` on <html> and the
  // only subscriber to `prefers-color-scheme`; its previous only caller
  // was the bar's theme button. Moving the CONTROL into the menu panel
  // without moving the HOOK out of it would have stopped the theme
  // being applied at all whenever the menu was shut. This component is
  // always mounted; only its panel is conditional.
  const { preference, effective, setTheme } = useTheme();
  // Null until the browser has actually offered an install, so the item
  // below is either real or absent -- never a control that does
  // nothing. Same always-mounted reasoning as the hooks around it: the
  // event fires once, early, and is captured at module scope.
  const installPrompt = useInstallPrompt();
  // Same reasoning as `useTheme` above: read the fire state and the
  // capability gate on the ALWAYS-MOUNTED component, not inside the
  // panel. Gating inside would mean the capability probe only started
  // when somebody opened the menu, so the item would pop in after the
  // menu was already on screen -- on the one control where hesitating
  // over whether it is there is the whole problem.
  const { openFireModePanel, isFireAlarmActive } = useFireAlarm();
  const { canTrigger, canReset } = useEmergencyCapabilities();
  const canReachFireMode = canTrigger || canReset;
  // The twin is an UNLISTED route -- deliberately absent from the side nav
  // while it is a showcase surface rather than a daily one. This is the one
  // way in that is not a typed URL, and it is scoped to the demo role: the
  // people running a showcase present as `service_operator`, and everybody
  // else keeps the route unlisted. There is no RoleGuard on /twin: the gate
  // is `TwinRoute` (`twinRouteDecision`, `screens/Twin/twin-route.ts`),
  // which shows the showcase only for a bare /twin with the demo role and
  // the flag on, and redirects everything else to the Overview's Building
  // view. Hiding the link is not a security control and is not treated as
  // one.
  const canSeeTwin =
    getRuntimeConfig().featureFlags.twinEnabled === true &&
    roles.includes('nexsoss:service_operator');
  // Site settings: supervisors and admins edit it; platform support reads
  // it (D12). The screen itself decides what is editable.
  const canOpenSiteSettings = hasAnyRole(roles, SITE_SETTINGS_VIEW_ROLES);
  const role = useHighestRole();
  // "View as…" is offered by the REAL role (platform_admin, support_l2,
  // support_l3), never by the previewed one -- otherwise a preview of a
  // site role would take away the way back out of it. `choices` is
  // empty for everyone else, which is the whole visibility rule.
  const { previewRole, choices: previewChoices, enterPreview, exitPreview } = useRolePreview();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | HTMLAnchorElement | null>>([]);
  const menuId = useId();
  void effective;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close the menu when the route changes (e.g. clicking Preferences).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Focus the first menu item when the menu opens, so arrow keys
  // immediately work without an extra Tab.
  useEffect(() => {
    if (!open) return;
    // Defer one tick so the menu element is in the DOM.
    // `.find`, not `[0]`: the emergency item is conditional, so index
      // 0 is a hole for anyone whose role cannot reach fire mode.
      const t = window.setTimeout(
        () => itemRefs.current.find(Boolean)?.focus(),
        0,
      );
    return () => window.clearTimeout(t);
  }, [open]);

  if (!user) return null;

  const initials = initialsFor(user.displayName);
  const roleText = previewRole ? `viewing as ${previewRoleLabel(previewRole)}` : roleLabel(role);

  /**
   * Arrow-key / Home / End navigation across the menu items. The
   * items live in a 2-element array (Preferences, Sign out) so we
   * use simple modulo arithmetic.
   */
  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const items = itemRefs.current.filter(Boolean) as HTMLElement[];
    if (items.length === 0) return;
    const activeIndex = items.findIndex((el) => el === document.activeElement);
    let nextIndex = activeIndex;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = (activeIndex + 1) % items.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = (activeIndex - 1 + items.length) % items.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = items.length - 1;
    } else {
      return;
    }
    items[nextIndex]?.focus();
  }

  return (
    <div className="nx-topbar__avatar-root" ref={rootRef}>
      <button
        type="button"
        className="nx-topbar__avatar-btn"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu for ${user.displayName}`}
        data-testid="topbar-avatar-btn"
      >
        <span className="nx-topbar__avatar-bubble" aria-hidden="true">
          {initials}
        </span>
        <span className="nx-topbar__avatar-text">
          <span className="nx-topbar__avatar-name">{user.displayName}</span>
          <span className="nx-topbar__avatar-role">{roleText}</span>
        </span>
        <span className="nx-topbar__avatar-chevron" aria-hidden="true">
          <IconChevronDown size={14} />
        </span>
      </button>
      {open && (
        <div
          id={menuId}
          className="nx-topbar__avatar-menu"
          role="menu"
          aria-label="Account"
          onKeyDown={onMenuKeyDown}
          data-testid="topbar-avatar-menu"
        >
          <div className="nx-topbar__avatar-menu-head" aria-hidden="true">
            <span className="nx-topbar__avatar-bubble nx-topbar__avatar-bubble--lg">
              {initials}
            </span>
            <span className="nx-topbar__avatar-menu-meta">
              <strong>{user.displayName}</strong>
              <em>{user.email}</em>
            </span>
          </div>
          {/* FIRST, and the first arrow-key stop. Moved here from the
              top bar on Akash's instruction after I argued against it:
              it is the only way into the fire panel while no alarm is
              running, so being one menu deep costs a click at the worst
              possible moment. What is left to soften that: it is the
              top item, it is styled as the loud one, `g x` opens the
              panel from anywhere without touching the menu, and while
              an alarm IS running `GlobalStatusBanner` puts its own
              button across the top of every screen. */}
          {canReachFireMode && (
            <button
              type="button"
              ref={(el) => {
                itemRefs.current[0] = el;
              }}
              className="nx-topbar__avatar-menu-item nx-topbar__avatar-menu-item--emergency"
              role="menuitem"
              tabIndex={-1}
              data-testid="topbar-menu-emergency"
              data-active={isFireAlarmActive ? 'true' : 'false'}
              onClick={() => {
                setOpen(false);
                openFireModePanel();
              }}
            >
              <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                <FlameGlyph />
              </span>
              <span>
                {isFireAlarmActive ? 'Fire mode active — recovery' : 'Emergency: fire mode'}
              </span>
              <kbd className="nx-topbar__avatar-menu-kbd" aria-hidden="true">
                g x
              </kbd>
            </button>
          )}
          {canSeeTwin && (
            <Link
              to="/twin"
              className="nx-topbar__avatar-menu-item"
              role="menuitem"
              tabIndex={-1}
              data-testid="topbar-menu-twin"
              onClick={() => setOpen(false)}
            >
              <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                <IconMonitor size={15} />
              </span>
              <span>Building view</span>
            </Link>
          )}
          <Link
            to="/profile"
            ref={(el) => {
              itemRefs.current[1] = el;
            }}
            className="nx-topbar__avatar-menu-item"
            role="menuitem"
            tabIndex={-1}
            data-testid="topbar-menu-preferences"
            onClick={() => setOpen(false)}
          >
            <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
              <IconSettings size={15} />
            </span>
            <span>Preferences</span>
          </Link>
          {/* The person's own command history (`/activity`). Every role
              has it; it was reachable only by typing the address until
              2026-10-31 (decided: add it to this menu). */}
          <Link
            to="/activity"
            ref={(el) => {
              itemRefs.current[2] = el;
            }}
            className="nx-topbar__avatar-menu-item"
            role="menuitem"
            tabIndex={-1}
            data-testid="topbar-menu-activity"
            onClick={() => setOpen(false)}
          >
            <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
              <IconFileText size={15} />
            </span>
            <span>My activity</span>
          </Link>
          {/* Per-site settings (Metrics' labour rate, targets, zone
              groups, go-live date): supervisors and admins edit, platform
              support reads. */}
          {canOpenSiteSettings && (
            <Link
              to="/site-settings"
              ref={(el) => {
                itemRefs.current[3] = el;
              }}
              className="nx-topbar__avatar-menu-item"
              role="menuitem"
              tabIndex={-1}
              data-testid="topbar-menu-site-settings"
              onClick={() => setOpen(false)}
            >
              <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                <IconSettings size={15} />
              </span>
              <span>Site settings</span>
            </Link>
          )}
          <button
            type="button"
            ref={(el) => {
              itemRefs.current[4] = el;
            }}
            className="nx-topbar__avatar-menu-item"
            role="menuitem"
            tabIndex={-1}
            data-testid="topbar-menu-report"
            onClick={() => {
              setOpen(false);
              onReportProblem();
            }}
          >
            <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
              <IconLifeBuoy size={15} />
            </span>
            <span>Report a problem</span>
          </button>
          {/* Only when the browser has actually offered an install --
              `useInstallPrompt` returns null otherwise, so this is never
              a control that does nothing. iOS has no equivalent event
              (Share -> Add to Home Screen is the only route and a page
              cannot trigger it), which is why the device runbook
              carries that path rather than a button pretending to. */}
          {installPrompt && (
            <button
              type="button"
              ref={(el) => {
                itemRefs.current[5] = el;
              }}
              className="nx-topbar__avatar-menu-item"
              role="menuitem"
              tabIndex={-1}
              data-testid="topbar-menu-install"
              onClick={() => {
                setOpen(false);
                void installPrompt.install();
              }}
            >
              <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                <IconDownload size={15} />
              </span>
              <span>Install app</span>
            </button>
          )}
          {/* `menuitemradio` rather than the old popover-inside-a-popover.
              A menu MAY contain radios and the container's arrow-key
              handler already walks them; a nested menu would have put
              two sets of document-level pointerdown and Escape listeners
              against each other, so closing the inner one closed both. */}
          <div className="nx-topbar__avatar-menu-group" role="group" aria-label="Appearance">
            <span className="nx-topbar__avatar-menu-label" aria-hidden="true">
              Appearance
            </span>
            {THEME_OPTIONS.map((opt, i) => (
              <button
                key={opt.value}
                type="button"
                ref={(el) => {
                  itemRefs.current[6 + i] = el;
                }}
                className={`nx-topbar__avatar-menu-item${
                  opt.value === preference ? ' nx-topbar__avatar-menu-item--active' : ''
                }`}
                role="menuitemradio"
                aria-checked={opt.value === preference}
                tabIndex={-1}
                data-testid={`topbar-menu-theme-${opt.value}`}
                onClick={() => setTheme(opt.value)}
              >
                <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                  <opt.Icon size={15} />
                </span>
                <span>{opt.label}</span>
                {opt.value === preference && <IconCheck size={14} />}
              </button>
            ))}
          </div>
          {/* "View as…": a look-only preview of the console as another
              role, for troubleshooting and training (role-preview.ts).
              Radios for the same reason as Appearance above. Picking
              one closes the menu, because the menu itself changes
              underneath it (the previewed role's fire item, Building
              view link and role line). */}
          {previewChoices.length > 0 && (
            <div
              className="nx-topbar__avatar-menu-group"
              role="group"
              aria-label="View as"
              data-testid="topbar-menu-view-as"
            >
              <span className="nx-topbar__avatar-menu-label" aria-hidden="true">
                View as…
              </span>
              {previewChoices.map((choice, i) => (
                <button
                  key={choice}
                  type="button"
                  ref={(el) => {
                    itemRefs.current[VIEW_AS_FIRST_INDEX + i] = el;
                  }}
                  className={`nx-topbar__avatar-menu-item${
                    choice === previewRole ? ' nx-topbar__avatar-menu-item--active' : ''
                  }`}
                  role="menuitemradio"
                  aria-checked={choice === previewRole}
                  tabIndex={-1}
                  data-testid={`topbar-menu-view-as-${choice.replace(/^nexsoss:/, '')}`}
                  onClick={() => {
                    setOpen(false);
                    if (choice === previewRole) exitPreview();
                    else enterPreview(choice);
                  }}
                >
                  <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                    <IconMonitor size={15} />
                  </span>
                  <span>{previewRoleLabel(choice)}</span>
                  {choice === previewRole && <IconCheck size={14} />}
                </button>
              ))}
              {previewRole && (
                <button
                  type="button"
                  ref={(el) => {
                    itemRefs.current[VIEW_AS_FIRST_INDEX + previewChoices.length] = el;
                  }}
                  className="nx-topbar__avatar-menu-item"
                  role="menuitem"
                  tabIndex={-1}
                  data-testid="topbar-menu-view-as-exit"
                  onClick={() => {
                    setOpen(false);
                    exitPreview();
                  }}
                >
                  <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
                    <IconRotateCcw size={15} />
                  </span>
                  <span>Back to my own role</span>
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            ref={(el) => {
              itemRefs.current[SIGN_OUT_INDEX] = el;
            }}
            className="nx-topbar__avatar-menu-item nx-topbar__avatar-menu-item--danger"
            role="menuitem"
            tabIndex={-1}
            data-testid="topbar-menu-signout"
            onClick={() => {
              setOpen(false);
              void logout().finally(() => navigate('/login', { replace: true }));
            }}
          >
            <span className="nx-topbar__avatar-menu-icon" aria-hidden="true">
              <IconLogOut size={15} />
            </span>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function TopBar({
  onToggleNav,
  navOpen = false,
}: {
  /** Phone-only. The button is hidden by CSS above 767px, so the
      handler is simply never reachable in the desktop layout. */
  onToggleNav?: () => void;
  navOpen?: boolean;
} = {}) {
  const { site, sites, setSite } = useActiveSite();
  const { muted, toggleMute } = useAlertNotificationsStable();
  // Held here rather than in the menu: the dialog's wrapper stays in
  // the bar for positioning, so the two are siblings and the state has
  // to live above both.

  // PUBLISHES THE BAR'S REAL HEIGHT as `--nx-topbar-h`. The bar is one
  // row on a desktop and three on a phone (site row, icon row,
  // scrolling stat row), and anything that has to sit BELOW it was
  // guessing: the fire-alarm banner used a hard-coded `top: 80px` and
  // landed on top of the wrapped bar on every phone, and the phone
  // popover cap is written as `calc(100vh - 100%)` for the same want.
  // A measured value cannot drift when the bar gains a row.
  //
  // Set on the document element, not the header, because the fire
  // overlay is a `position: fixed` layer and so is not a descendant of
  // anything the header could scope a variable to.
  //
  // The "View as role" banner sits directly ABOVE the bar, so the value
  // published is the bar's BOTTOM edge rather than its height: with no
  // banner the two are the same number, and with one the fixed layers
  // still start below everything at the top. Both elements are
  // observed, and the effect re-runs when the banner comes or goes.
  const barRef = useRef<HTMLElement | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const { previewRole } = useRolePreview();
  useEffect(() => {
    const el = barRef.current;
    // jsdom has no ResizeObserver unless a test installs one, and the
    // fallback in the CSS (`var(--nx-topbar-h, 80px)`) is the same
    // value the banner used before this existed.
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publish = () => {
      document.documentElement.style.setProperty(
        '--nx-topbar-h',
        `${Math.round(el.getBoundingClientRect().bottom)}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    if (bannerRef.current) ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [previewRole]);

  return (
    <>
    <RolePreviewBanner ref={bannerRef} />
    <header className="nx-topbar" ref={barRef}>
      <div className="nx-topbar__left">
        {/* Phone-only nav toggle. Hidden by CSS above 767px rather than
            branched on in JS: a width branch here would need a resize
            listener and could disagree with the media query at the
            boundary, which is how you get a drawer with no way to open
            it on exactly one screen size. */}
        <button
          type="button"
          className="nx-shell__nav-toggle"
          data-testid="shell-nav-toggle"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          onClick={onToggleNav}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
               fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <label className="nx-topbar__site">
          <span className="nx-topbar__site-label">Site</span>
          <span className="nx-topbar__site-select-wrap">
            <select
              className="nx-topbar__site-select"
              value={site?.id ?? ''}
              onChange={(e) => setSite(e.target.value as SiteId)}
              aria-label="Active site"
              disabled={sites.length === 0}
            >
              {sites.length === 0 && <option value="">No sites assigned</option>}
              {sites.map((s) => (
                <option key={s.site} value={s.site}>
                  {s.label}
                </option>
              ))}
            </select>
            <IconChevronDown size={13} className="nx-topbar__site-chevron" />
          </span>
        </label>
        <LiveStatusDot />
        <TrajectoryFeedChip />
      </div>
      <TopBarStats />
      {/* TUCKED (`use-scroll-tuck.ts`, `AppShell.css`) the bar is one slim
          row: the menu (phone), the site, LIVE (laptop), and from this
          cluster the fire control (phone), the bell and the avatar.
          Everything marked `data-tuck="hide"` -- the stat chips, mute,
          help and favourites -- gives way, as do LIVE on a phone and the
          signed-in name on a laptop, and all of it comes back on any
          upward scroll. The fire control stays: on a phone it is the one
          control where a lost second matters (see `PhoneFireButton`).
          The "viewing as" banner above the bar is not part of it and
          never hides. */}
      <div className="nx-topbar__right">
        {/* FIRST in the cluster, so it is the leftmost control on the
            phone's icon row and the furthest from the destructive
            neighbours it sits beside. */}
        <PhoneFireButton />
        <button
          type="button"
          className="nx-topbar__btn nx-topbar__audio-toggle"
          data-tuck="hide"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute alert audio' : 'Mute alert audio'}
          aria-pressed={muted}
          title={muted ? 'Unmute alerts' : 'Mute alerts'}
          data-testid="topbar-mute-toggle"
        >
          {muted ? <IconVolumeX size={16} /> : <IconVolume2 size={16} />}
        </button>
        <HelpToggle />
        <FavoritesMenu />
        <AlertBell />
        <ReportProblemHost />
        <UserAvatarMenu onReportProblem={() => openReportProblem()} />
      </div>
    </header>
    </>
  );
}
